import { useCallback } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { useBrandName } from '@jotta/grpc-connect-client/config'
import {
  useAcceptFamilyInvite,
  useGetCustomer,
} from '@jotta/grpc-connect-client/customer'
import { useFamilyInvite } from '@jotta/grpc-connect-client/invite'
import { contentRefLink } from '@jotta/grpc-connect-client/util'
import type { Customer } from '@jotta/grpc-connect-openapi/customer'
import {
  type GetFamilyInviteResponse,
  GetFamilyInviteResponse_Ineligible_Reason,
  type GetFamilyInviteResponse_Invite,
} from '@jotta/grpc-connect-openapi/invite'
import { BaseErrorPage } from '@jotta/ui/BaseErrorPage'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

import { CreateUser } from '../../auth/CreateUser'
import { CTAButton } from '../../auth/CTAButton'
import { EntryLayout } from '../../components/Layout/EntryLayout'
import { PublicLayout } from '../../components/Layout/PublicLayout'

function FamilyOwner({
  inviteCode,
  customer,
  invite,
}: {
  inviteCode: string
  customer: Customer
  invite: GetFamilyInviteResponse_Invite
}) {
  const loginUrl = invite.redirectUrl || '/api/login'
  const link = contentRefLink({
    case: 'familyInvite',
    value: {
      inviteCode: inviteCode,
    },
  })
  const email = <span className="font-bold">{customer.email}</span>
  return (
    <>
      <h1 className="text-2xl">
        <Trans>Invite your family</Trans>
      </h1>
      <p>
        <Trans>
          You are logged in as {email}, and are the owner of the family
          subscription!
        </Trans>
      </p>
      <p>
        <Trans>Join with a different account?</Trans>&nbsp;
        <a href={loginUrl} className="!text-signup-link underline">
          <Trans>Log in</Trans>
        </a>
      </p>
    </>
  )
}

function FamilyInviteExistingAccount({
  invite,
  customer,
  inviteCode,
}: {
  invite: GetFamilyInviteResponse_Invite
  customer: Customer
  inviteCode: string
}) {
  const familyName = invite.familyName
  const email = customer.email
  const loginUrl = invite.redirectUrl || '/api/login'
  const brandName = useBrandName()
  const acceptInviteMutation = useAcceptFamilyInvite()
  const navigate = useNavigate()
  const acceptInvite = useCallback(() => {
    acceptInviteMutation.mutate(
      { inviteCode },
      {
        onSuccess: () => {
          navigate('/web')
        },
      },
    )
  }, [acceptInviteMutation, inviteCode, navigate])

  return (
    <>
      <h1 className="text-2xl">
        <Trans>
          Join the <strong>{familyName} family</strong> on {brandName} with{' '}
          <strong>{email}</strong>
        </Trans>
      </h1>

      <div className="flex flex-col gap-4 rounded bg-white p-6">
        <CTAButton
          onClick={acceptInvite}
          loading={
            acceptInviteMutation.isPending || acceptInviteMutation.isSuccess
          }
        >
          <Trans>Continue</Trans>
        </CTAButton>
        <p>
          <Trans>Join with a different account?</Trans>&nbsp;
          <a href={loginUrl} className="!text-signup-link underline">
            <Trans>Log in</Trans>
          </a>
        </p>
      </div>
    </>
  )
}

function FamilyInviteNewAccount({
  inviteCode,
  invite,
  brandName,
}: {
  inviteCode: string
  invite: GetFamilyInviteResponse_Invite
  brandName: string
}) {
  const {
    familyName,
    inviteeEmail: email,
    inviteeName: name,
    redirectUrl,
    customerGroupCode,
  } = invite

  if (redirectUrl) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl">
          <Trans>
            Are you ready to join <strong>the {familyName} family</strong> on{' '}
            {brandName}?
          </Trans>
        </h1>
        <a className="btn btn-primary" href={redirectUrl}>
          <Trans>Continue</Trans>
        </a>
      </div>
    )
  }

  return (
    <>
      <h1 className="text-2xl">
        <Trans>
          Create a new account to join <strong>the {familyName} family</strong>{' '}
          on {brandName}
        </Trans>
      </h1>
      <CreateUser
        type="private"
        className="block pt-6"
        prefill={{ name, email }}
        familyInviteId={inviteCode}
        customerGroupOverride={customerGroupCode}
        federationIntent={{ case: 'signup', value: {} }}
        keepRouteOnSuccess
        allowAppPromotion
      />
    </>
  )
}

export function FamilyInviteSwitch({
  response,
  customer,
  inviteCode,
  brandName,
}: {
  response: GetFamilyInviteResponse
  customer?: Customer
  inviteCode: string
  brandName: string
}) {
  const { result } = response

  switch (result.case) {
    case 'accepted':
      if (result.value.byCurrentUser) {
        return (
          <div className="flex flex-col items-center gap-8">
            <h1 className="text-2xl">
              <Trans>Welcome to the family!</Trans>
            </h1>
            <p>
              <Trans>
                You can now share the subscription with the rest of your family.
              </Trans>
            </p>
            <Link className="btn btn-primary" to="/web/sync">
              <Trans>Continue</Trans>
            </Link>
          </div>
        )
      } else {
        return (
          <BaseErrorPage
            title={t`The invitation has already been used`}
            reportError={undefined}
          >
            <Trans>
              If you already accepted this invitation with a different user,{' '}
              <a href="/api/logout">log in</a> to switch accounts. Else contact
              the person who sent you this invite and ask if he/she can send a
              new one.
            </Trans>
          </BaseErrorPage>
        )
      }

    case 'cancelled':
      return (
        <BaseErrorPage
          title={t`The invitation has been cancelled`}
          reportError={undefined}
        >
          <Trans>This invitation has been canceled by the family owner.</Trans>
        </BaseErrorPage>
      )

    case 'ineligible': {
      const alreadyInFamily =
        result.value.reason ===
        GetFamilyInviteResponse_Ineligible_Reason.ALREADY_IN_FAMILY
      return (
        <BaseErrorPage
          title={
            alreadyInFamily
              ? t`You are already in a family`
              : t`This invitation cannot be used`
          }
          reportError={undefined}
        >
          {alreadyInFamily ? (
            <Trans>
              You already belong to a family subscription, so you cannot accept
              this invite. If you meant to join with a different account,{' '}
              <a href="/api/logout">log in</a> to switch accounts.
            </Trans>
          ) : (
            <Trans>
              This invitation cannot be used with your current account.
            </Trans>
          )}
        </BaseErrorPage>
      )
    }

    case 'notFound':
      return (
        <BaseErrorPage title={t`Invitation not found`} reportError={undefined}>
          <p>
            <Trans>
              Please check the email with the invitation and try to click on the
              link again.
            </Trans>
          </p>
          <p>
            <a href="/">
              <Trans>Click here to go back to the homepage.</Trans>
            </a>
          </p>
        </BaseErrorPage>
      )

    case 'invite': {
      const invite = result.value
      if (customer) {
        return invite.fromCurrentUser ? (
          <FamilyOwner
            inviteCode={inviteCode}
            invite={invite}
            customer={customer}
          />
        ) : (
          <FamilyInviteExistingAccount
            invite={invite}
            customer={customer}
            inviteCode={inviteCode}
          />
        )
      } else {
        return (
          <FamilyInviteNewAccount
            inviteCode={inviteCode}
            invite={invite}
            brandName={brandName}
          />
        )
      }
    }
    case undefined:
      throw new Error('Invalid response')

    default:
      return exhaustiveGuard(result)
  }
}

export const FamilyInviteRoute = observer(function FamilyInviteRoute() {
  const { inviteCode = '' } = useParams()
  const { data: inviteData } = useFamilyInvite(inviteCode)
  const { data: customerData, isLoading: isCustomerLoading } = useGetCustomer()
  const brandName = useBrandName()

  if (!inviteData || isCustomerLoading || !brandName) {
    return <LoadingOverlay id="FamilyInviteRoute" open />
  }

  return (
    <EntryLayout>
      <FamilyInviteSwitch
        inviteCode={inviteCode}
        response={inviteData}
        customer={customerData}
        brandName={brandName}
      />
    </EntryLayout>
  )
})
